import {createSelector} from 'reselect'

import {
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  onlyIfForm,
  getState,
  useSelector,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {saveUserSettings, userSettingSelector} from '../../../data/me.js'
import {openSyncLoadingTab} from '../../AbodeForm/abodeFormFunctions.js'
import ButtonSendToPrinter, {
  sendToPrintNode,
  startSendingToPrintNode,
} from '../../../common/components/Button/ButtonSendToPrinter.js'
import {
  LAYOUT_STOCKTAKE,
  layoutsByTypeSortedByName,
} from '../../../data/layout.js'
import {getUTCOffset} from '../../../common/date.js'
import {abodeRenderPOST} from '../../../common/abode.js'
import {openFileInTab} from '../../../common/openInTab.js'
import {stocktakePrinterIDSelector} from '../../../data/printerSettings.js'
import {defaultHashParamsSelector} from '../../StocktakeDetailPage/stocktakeDetailFunctions.js'

const MODAL_FORM = 'stocktake_print_modal'
const STOCKTAKE__PRINT__SHOW_BARCODES = 'stocktake.print.show_barcodes'
const STOCKTAKE__PRINT__SHOW_IMAGES = 'stocktake.print.show_images'

export function showStocktakeBarcodesSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__PRINT__SHOW_BARCODES,
    defaultValue: true,
  })
}

export function showStocktakeImagesSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__PRINT__SHOW_IMAGES,
    defaultValue: true,
  })
}

export function showStocktakePrintModal(referenceID, query) {
  setForm(MODAL_FORM, {
    referenceID,
    query: query || defaultHashParamsSelector(getState()),
    show_barcodes: showStocktakeBarcodesSelector(getState()),
    show_images: showStocktakeImagesSelector(getState()),
    isPrinting: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

function renderStocktakePayloadSelector(state) {
  const {referenceID, query, show_barcodes, show_images} =
    modalFormSelector(state)
  const layouts = layoutsByTypeSortedByName(state)[LAYOUT_STOCKTAKE]
  const layoutID = layouts?.[0].id

  const render = [
    'StocktakeLayout',
    {
      reference_id: referenceID,
      stocktake: {$: ['getStocktake', referenceID]},
      lines: {$: ['getStocktakeLines', referenceID, query]},
      show_barcodes,
      show_images,
    },
  ]

  const _state = {
    utc_offset: getUTCOffset(),
  }

  return {
    file_name: 'stocktake.pdf',
    render,
    state: _state,
    ...(layoutID ? {layout_id: layoutID} : null),
  }
}

async function saveStocktakePrintSettings() {
  const {show_barcodes, show_images} = modalFormSelector(getState())

  await saveUserSettings({
    [STOCKTAKE__PRINT__SHOW_BARCODES]: show_barcodes,
    [STOCKTAKE__PRINT__SHOW_IMAGES]: show_images,
  })
}

export async function asyncPrintStocktake(tab) {
  try {
    updateModalForm({isPrinting: true})

    await saveStocktakePrintSettings()

    const payload = renderStocktakePayloadSelector(getState())

    const file = await abodeRenderPOST(payload)

    openFileInTab(file, tab)

    showMessageToast('Stocktake Printed')

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: `Error printing stocktake: ${
        err.message || err.error_message
      }`,
      isPrinting: false,
    })
  }
}

function printStocktake() {
  const tab = openSyncLoadingTab()

  if (!tab) {
    return
  }

  asyncPrintStocktake(tab)
}

async function handleButtonSentToPrintClick({canPrint, printButtonFormKey}) {
  // when we need to navigate to settings to setup printers
  if (!canPrint) {
    closeModal()

    return
  }

  try {
    const printerID = stocktakePrinterIDSelector(getState())

    updateModalForm({
      serverError: null,
    })

    startSendingToPrintNode(printButtonFormKey)

    await saveStocktakePrintSettings()

    const payload = renderStocktakePayloadSelector(getState())

    const file = await abodeRenderPOST(payload)

    await sendToPrintNode({
      formKey: printButtonFormKey,
      pdfFile: file,
      pdfName: payload.file_name,
      printerID,
      documentCount: 1,
    })

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.error_message || err.message,
    })
  }
}

function StocktakePrintModal({form}) {
  const stocktakePrinterID = useSelector(stocktakePrinterIDSelector)

  return (
    <ConfirmModal
      className="modal--fit-screen-sm"
      title="Print Stocktake"
      confirmText="Print"
      cancelText="Cancel"
      onConfirm={() => printStocktake()}
      onCancel={() => closeModal()}
      middleButtons={
        <div className="margin-right-10">
          <ButtonSendToPrinter
            className="margin-bottom-5"
            title="Stocktake"
            printerID={stocktakePrinterID}
            documentCount={1}
            onClick={({canPrint, printButtonFormKey}) =>
              handleButtonSentToPrintClick({
                canPrint,
                printButtonFormKey,
              })
            }
          />
        </div>
      }
      isSaving={form.isPrinting}
      error={form.serverError}
    >
      <dl className="list margin-bottom-30">
        <dt className="list__title fs-01 lh-md margin-bottom-15">
          Print Settings
        </dt>
        <dd className="list__item margin-bottom-20">
          <Checkbox
            id="show_barcodes"
            label="Show Product Barcodes"
            checked={form.show_barcodes}
            onChange={(show_barcodes) => updateModalForm({show_barcodes})}
          />
        </dd>
        <dd className="list__item margin-bottom-20">
          <Checkbox
            id="show_images"
            label="Show Product Images"
            checked={form.show_images}
            onChange={(show_images) => updateModalForm({show_images})}
          />
        </dd>
      </dl>
    </ConfirmModal>
  )
}

export default onlyIfForm(StocktakePrintModal, modalFormSelector)
